'use client';

import { useMemo, useState } from 'react';
import { PlusIcon } from 'lucide-react';
import isEqual from 'react-fast-compare';
import { toast } from 'sonner';

import { Select } from '@/components/atoms/select';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ISSUE_CATEGORY_OPTIONS, ISSUE_COVERAGE_OPTIONS } from '@/consts/enums';
import { cn } from '@/lib/utils';

type ApiIssue = ApiSchemas['IssueRead'];

export function CreatePestDialog() {
  const [open, setOpen] = useState(false);
  const { api } = useApiV1Ctx();
  const { officeId, refetch } = useOfficeCtx();
  const { mutate, isPending } = api.issues.createIssuesPost.useMutation(
    {},
    {
      onSuccess: () => {
        toast.success('Pest created');
        refetch();
        setOpen(false);
      },
    }
  );
  const [values, setValues] = useState<PestFormValues>({
    name: '',
    description: '',
    category: 'INSECT',
    coverage: 'ANY',
  });

  const onChange = (values: Partial<PestFormValues>) => {
    setValues((prev) => ({ ...prev, ...values }));
  };

  const onSubmit = () => {
    if (!values.name) {
      toast.error('Name is required');
      return;
    }

    mutate({
      office_id: officeId,
      name: values.name,
      description: values.description,
      category: values.category,
      coverage: 'ANY',
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size='sm'>
          <PlusIcon className='size-4' />
          Add Pest
        </Button>
      </DialogTrigger>
      <DialogContent showClose>
        <DialogHeader>
          <DialogTitle>Add Pest</DialogTitle>
          <DialogDescription>Add a new pest to your office.</DialogDescription>
        </DialogHeader>
        <PestFormInputs className='py-4' values={values} onChange={onChange} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button type='submit' onClick={onSubmit} disabled={!values.name || isPending}>
            {isPending ? 'Creating...' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface EditPestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  issue: ApiIssue;
}

export function EditPestDialog({ open, onOpenChange, issue }: EditPestDialogProps) {
  const { api } = useApiV1Ctx();
  const { refetch } = useOfficeCtx();
  const { mutate, isPending } = api.issues.updateIssuesIssueIdPut.useMutation(
    {
      path: { issue_id: issue.id },
    },
    {
      onSuccess: () => {
        toast.success('Pest updated');
        refetch();
        onOpenChange(false);
      },
    }
  );

  const [values, setValues] = useState<PestFormValues>({
    name: issue.name,
    description: issue.description ?? '',
    category: issue.category,
    coverage: issue.coverage,
  });
  const isDisabled = useMemo(() => {
    if (!values.name) return true;
    if (isPending) return true;
    return isEqual(values, {
      name: issue.name,
      description: issue.description ?? '',
      category: issue.category,
    });
  }, [values, isPending, issue]);

  const onChange = (values: Partial<PestFormValues>) => {
    setValues((prev) => ({ ...prev, ...values }));
  };

  const onSubmit = () => {
    if (!values.name) {
      toast.error('Name is required');
      return;
    }

    mutate({ name: values.name, description: values.description, category: values.category, coverage: 'ANY' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showClose>
        <DialogHeader>
          <DialogTitle>Edit Pest</DialogTitle>
          <DialogDescription>Edit the pest details.</DialogDescription>
        </DialogHeader>
        <PestFormInputs className='py-4' values={values} onChange={onChange} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button type='submit' onClick={onSubmit} disabled={isDisabled}>
            {isPending ? 'Updating...' : 'Update'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface PestFormValues {
  name: string;
  description: string;
  category: ApiIssue['category'];
  coverage: ApiIssue['coverage'];
}

interface PestFormInputsProps {
  className?: string;
  values: PestFormValues;
  onChange: (values: Partial<PestFormValues>) => void;
}

function PestFormInputs({ className, values, onChange }: PestFormInputsProps) {
  return (
    <div className={cn('grid gap-4 py-4', className)}>
      <div className='grid grid-cols-2 items-center gap-4'>
        <div className='w-full space-y-2'>
          <Label htmlFor='name'>Name</Label>
          <Input
            id='name'
            value={values.name}
            onChange={(e) => onChange({ name: e.target.value })}
            className='col-span-3'
          />
        </div>
        <div className='w-full space-y-2'>
          <Label htmlFor='category'>Category</Label>
          <Select
            placeholder='Select pest category'
            options={ISSUE_CATEGORY_OPTIONS}
            value={values.category}
            onChange={(v) => onChange({ category: v })}
            className='w-full'
          />
        </div>
        <div className='w-full space-y-2'>
          <Label htmlFor='coverage'>Coverage</Label>
          <Select
            placeholder='Select coverage'
            options={ISSUE_COVERAGE_OPTIONS}
            value={values.coverage}
            onChange={(v) => onChange({ coverage: v })}
            className='w-full'
          />
        </div>
      </div>
      <div className='space-y-2'>
        <Label htmlFor='description'>Description</Label>
        <Textarea
          id='description'
          value={values.description}
          onChange={(e) => onChange({ description: e.target.value })}
          className='w-full'
        />
      </div>
    </div>
  );
}
